import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Chip,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const ManageProductsDialog = ({ open, onClose, client }) => {
  const { getProducts, getUserProducts, postUserProducts, patchUserProduct } =
    useApi();
  const { stripeAccountId } = useUserInfo();
  const [products, setProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});

  const loadProductsData = async () => {
    setProductsLoading(true);
    try {
      const [allProducts, clientProducts, clientPurchasedProducts] =
        await Promise.all([
          getProducts(stripeAccountId),
          getUserProducts(client.id, "assigned"),
          getUserProducts(client.id, "purchased"),
        ]);
      setProducts(allProducts);
      setAssignedProducts(clientProducts);
      setClientPurchasedProducts(clientPurchasedProducts);
      const assignedPriceIds = clientProducts
        .filter((p) => p.type === "assigned")
        .map((p) => p.priceId);

      setSelectedProducts(assignedPriceIds);
    } catch (error) {
      console.error("Failed to load products:", error);
    }
    setProductsLoading(false);
  };

  useEffect(() => {
    if (open && client) {
      loadProductsData();
    }
  }, [open, client]);

  const handleAssignProducts = async () => {
    try {
      const items = selectedProducts.map((priceId) => {
        const product = products.find((p) =>
          p.prices.some((price) => price.priceId === priceId)
        );
        return {
          productId: product.productId,
          priceId: priceId,
        };
      });

      await postUserProducts({
        items,
        type: "assigned",
        userId: client.id,
      });
      onClose();
    } catch (error) {
      console.error("Failed to assign products:", error);
      alert("Failed to assign products. Please try again.");
    }
  };

  const handleQuantityChange = async (priceId, newValue) => {
    try {
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "updateQuantity",
        value: parseInt(newValue),
      });

      setClientPurchasedProducts((prev) =>
        prev.map((product) =>
          product.priceId === priceId
            ? { ...product, quantityLeft: parseInt(newValue) }
            : product
        )
      );

      setQuantityValues((prev) => ({
        ...prev,
        [priceId]: newValue,
      }));
    } catch (error) {
      console.error("Failed to update quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Products for {client?.name}</DialogTitle>
      <DialogContent>
        {productsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="center">Assign</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) =>
                  product.prices.map((price) => {
                    const isPurchased = clientPurchasedProducts.some(
                      (up) => up.priceId === price.priceId
                    );
                    const isAssigned = assignedProducts.some(
                      (up) => up.priceId === price.priceId && !isPurchased
                    );
                    const purchasedProduct = clientPurchasedProducts.find(
                      (up) => up.priceId === price.priceId
                    );

                    return (
                      <TableRow key={price.priceId}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{price.metadata.description}</TableCell>
                        <TableCell align="right">
                          ${(price.unitAmount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased ? (
                            <Chip
                              label="Purchased"
                              color="success"
                              size="small"
                            />
                          ) : isAssigned ? (
                            <Chip
                              label="Assigned"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Not Active"
                              color="default"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased &&
                            purchasedProduct?.quantityLeft !== undefined && (
                              <TextField
                                type="number"
                                size="small"
                                value={
                                  quantityValues[price.priceId] ??
                                  purchasedProduct.quantityLeft
                                }
                                onChange={(e) =>
                                  setQuantityValues((prev) => ({
                                    ...prev,
                                    [price.priceId]: e.target.value,
                                  }))
                                }
                                onBlur={(e) =>
                                  handleQuantityChange(
                                    price.priceId,
                                    e.target.value
                                  )
                                }
                                inputProps={{ min: 0 }}
                                sx={{ width: "80px" }}
                              />
                            )}
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={
                              isAssigned ||
                              selectedProducts.includes(price.priceId)
                            }
                            onChange={() => {
                              setSelectedProducts((prev) =>
                                prev.includes(price.priceId)
                                  ? prev.filter((id) => id !== price.priceId)
                                  : [...prev, price.priceId]
                              );
                            }}
                            disabled={isPurchased || isAssigned}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleAssignProducts}
          variant="contained"
          color="primary"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageProductsDialog;
